import React from 'react';

const fmt = (n) => (n == null ? '-' : Number(n).toLocaleString('ko-KR'));
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '-');
const fmtEventCount = (n) => (Number(n || 0) > 0 ? `+${fmt(n)}` : '-');

function paymentLabel(row) {
  const label = { cash: '현금', card: '카드', mixed: '혼합' }[row?.payment_method];
  if (label) return label;
  if (String(row?.card_payment_status || '').trim()) return '카드';
  return '-';
}

function receiptCancelText(row) {
  const number = String(row?.cash_receipt_cancel_approval_no || '').trim();
  const amount = Number(row?.cash_receipt_cancel_amount || 0);
  if (!number && amount === 0 && !row?.has_missing_cash_receipt_cancel) return '';
  return `${number || '번호 없음'} · ${fmt(amount)}`;
}

function amountColor(value) {
  return Number(value || 0) < 0 ? '#dc2626' : 'var(--text)';
}

function MoneyCell({ label, value, strong = false, tone = false }) {
  return (
    <div style={moneyCellStyle}>
      <span style={moneyLabelStyle}>{label}</span>
      <span style={{ ...moneyValueStyle, fontWeight: strong ? 800 : 700, color: tone ? amountColor(value) : 'var(--text)' }}>
        {fmt(value)}
      </span>
    </div>
  );
}

export default function MobileReservationList({ rows, onOpen }) {
  return (
    <div style={listStyle}>
      {rows.map((row) => {
        const hasRevenueExtra = Number(row.revenue_extra_event_count || 0) > 0 || Number(row.revenue_extra_event_amount || 0) !== 0;
        const hasCashReceipt = String(row.cash_receipt_issue_no || '').trim() || Number(row.cash_receipt_amount || 0) !== 0;
        const cashReceiptCancelText = receiptCancelText(row);
        return (
          <button
            key={row.travel_code}
            type="button"
            onClick={() => onOpen(row.travel_code)}
            style={cardStyle}
          >
            <div style={topRowStyle}>
              <span style={codeStyle}>{row.travel_code}</span>
              <span style={dateStyle}>{fmtDate(row.reservation_date)}</span>
            </div>

            <div style={nameRowStyle}>
              <strong style={nameStyle}>{row.customer_name || '-'}</strong>
              <span style={chipStyle}>{paymentLabel(row)}</span>
            </div>

            <div style={moneyGridStyle}>
              <MoneyCell label="항공" value={row.flight_total} />
              <MoneyCell label="렌트" value={row.car_total} />
              <MoneyCell label="호텔" value={row.hotel_total} />
              <MoneyCell label="고객지불" value={row.customer_paid} strong />
              <MoneyCell label="차액" value={row.difference} tone />
              <MoneyCell label="수익" value={row.profit} tone strong />
            </div>

            {hasRevenueExtra && (
              <div style={eventBoxStyle}>
                <div style={eventItemStyle}>
                  <span style={moneyLabelStyle}>수익이벤트</span>
                  <span style={eventCountStyle}>{fmtEventCount(row.revenue_extra_event_count)}</span>
                </div>
                <MoneyCell label="이벤트반영" value={row.revenue_extra_event_amount} tone strong />
              </div>
            )}

            {hasCashReceipt && (
              <div style={receiptStyle}>
                <span>현금영수증</span>
                <span>{row.cash_receipt_issue_no || '-'} · {fmt(row.cash_receipt_amount)}</span>
              </div>
            )}

            {cashReceiptCancelText && (
              <div style={{ ...receiptStyle, color: row.has_missing_cash_receipt_cancel ? '#dc2626' : 'var(--text-muted)' }}>
                <span>현영취소</span>
                <span>{cashReceiptCancelText}</span>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

const listStyle = {
  display: 'grid',
  gap: 10,
  padding: 10,
  minWidth: 0,
};

const cardStyle = {
  width: '100%',
  display: 'grid',
  gap: 10,
  padding: 12,
  border: '1px solid var(--border)',
  borderRadius: 10,
  background: 'var(--bg)',
  color: 'var(--text)',
  textAlign: 'left',
  cursor: 'pointer',
  minWidth: 0,
  boxSizing: 'border-box',
};

const topRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
};

const codeStyle = {
  fontSize: 13,
  fontWeight: 800,
  color: 'var(--std-accent-text, #1d7de6)', // 디자인 표준 강조색 (보라 accent 제거)
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const dateStyle = {
  flex: '0 0 auto',
  fontSize: 12,
  color: 'var(--text-muted)',
};

const nameRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  minWidth: 0,
};

const nameStyle = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 15,
};

const chipStyle = {
  flex: '0 0 auto',
  border: '1px solid var(--border)',
  borderRadius: 999,
  padding: '3px 8px',
  fontSize: 11,
  color: 'var(--text-muted)',
  background: 'var(--bg-card)',
};

const moneyGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 8,
};

const eventBoxStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 8,
  borderTop: '1px solid var(--border)',
  paddingTop: 10,
};

const eventItemStyle = {
  minWidth: 0,
  display: 'grid',
  gap: 2,
};

const eventCountStyle = {
  fontSize: 13,
  lineHeight: 1.25,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 800,
  color: 'var(--std-accent-text, #1d7de6)', // 디자인 표준 강조색 (보라 accent 제거)
};

const moneyCellStyle = {
  minWidth: 0,
  display: 'grid',
  gap: 2,
};

const moneyLabelStyle = {
  fontSize: 11,
  color: 'var(--text-muted)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const moneyValueStyle = {
  fontSize: 13,
  lineHeight: 1.25,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const receiptStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
  borderTop: '1px solid var(--border)',
  paddingTop: 10,
  fontSize: 12,
  color: 'var(--text-muted)',
  minWidth: 0,
};
